import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { BaseResponseDto } from '../types/dtos/base.dto';
import {
  DeviceProfileDto,
  DeviceProfileResponseDto,
  DeviceProfileUpdateDto,
  DevicesProfilesResponseDto,
} from '../types/dtos/deviceProfile.dto';
import { WebsocketResponseDto } from '../types/dtos/websocket.dto';
import { DomainEnum } from '../types/enums/websocket.enum';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceProfileService extends BaseService {
  private _devicesProfileSubject: BehaviorSubject<DeviceProfileDto[]> = new BehaviorSubject<DeviceProfileDto[]>([]);
  public devicesProfile$: Observable<DeviceProfileDto[]> = this._devicesProfileSubject.asObservable();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _authService: AuthService,
    private readonly _websocketService: WebsocketService,
  ) {
    super();
    this._init()
      .then();
  }

  private async _init() {
    await this._authService.waitUntilLogin();
    if (this._devicesProfileSubject.value.length === 0) {
      this.fetchDeviceProfiles();
      this.listenToDeviceProfileUpdates();
    }
  }

  public getCurrentList(): DeviceProfileDto[] {
    return this._devicesProfileSubject.value;
  }

  public addDeviceProfileToState(deviceProfile: DeviceProfileDto): void {
    const current: DeviceProfileDto[] = this._devicesProfileSubject.getValue();

    this._devicesProfileSubject.next([
      deviceProfile,
      ...current,
    ]);
  }

  public updateDeviceProfileInState(deviceProfile: DeviceProfileDto): void {
    const current: DeviceProfileDto[] = this._devicesProfileSubject.getValue();

    const updated: DeviceProfileDto[] = current.map((entry: DeviceProfileDto) =>
      entry.id === deviceProfile.id
        ? {...deviceProfile}
        : {...entry},
    );

    this._devicesProfileSubject.next(updated);
  }

  public fetchDeviceProfiles(): void {
    this._httpClient.get<DevicesProfilesResponseDto>(`${ this._baseUrl }/deviceProfiles`)
      .subscribe({
        next: (result: DevicesProfilesResponseDto) => {
          let deviceProfiles: DeviceProfileDto[] = result.data ?? [];

          if (!deviceProfiles) return;

          this._devicesProfileSubject.next(deviceProfiles);
        },
        error: (error) => console.error('Error fetching devices:', error),
      });
  }

  public addDeviceProfile(deviceProfileUpdate: DeviceProfileUpdateDto): Observable<BaseResponseDto<DeviceProfileDto>> {
    return this._httpClient.post<BaseResponseDto<DeviceProfileDto>>(
      `${ this._baseUrl }/deviceProfile`,
      deviceProfileUpdate,
    )
      .pipe(take(1));
  }

  public updateDeviceProfile(
    deviceProfileId: number,
    deviceProfileUpdate: DeviceProfileUpdateDto,
  ): Observable<BaseResponseDto<DeviceProfileDto>> {
    return this._httpClient.put<BaseResponseDto<DeviceProfileDto>>(
      `${ this._baseUrl }/deviceProfile/${ deviceProfileId }`,
      deviceProfileUpdate,
    )
      .pipe(take(1));
  }

  public fetchDeviceProfile(deviceProfileId: number): void {
    this._httpClient.get<DeviceProfileResponseDto>(`${ this._baseUrl }/deviceProfile/${ deviceProfileId }`)
      .subscribe({
        next: (result: DeviceProfileResponseDto) => {
          if (!result.data) return;

          result.data = result.data as DeviceProfileDto;

          const currentDeviceProfile: DeviceProfileDto[] = this._devicesProfileSubject.value;
          const newDeviceProfile: DeviceProfileDto[] = currentDeviceProfile.filter((d: DeviceProfileDto) => d.id !== deviceProfileId);

          newDeviceProfile.push(result.data);

          this._devicesProfileSubject.next(newDeviceProfile);
        },
        error: (error) => console.error('Error fetching device profile:', error),
      });
  }

  public getDeviceProfileById(id: number): DeviceProfileDto | null {
    return this._devicesProfileSubject.value.find((d: DeviceProfileDto) => d.id === id) || null;
  }

  public deleteDeviceProfile(id: number): Observable<BaseResponseDto<void>> {
    return this._httpClient.delete<BaseResponseDto<void>>(
      `${ this._baseUrl }/deviceProfile/${ id }`,
    );
  }

  public removeDeviceProfileFromState(id: number): void {
    const current: DeviceProfileDto[] = (this._devicesProfileSubject.getValue() ?? [])
      .filter((entry): entry is DeviceProfileDto => !!entry);

    this._devicesProfileSubject.next(
      current.filter((entry: DeviceProfileDto) => entry.id !== id),
    );
  }

  public addAlarmRule(deviceProfileId: number, alarmRuleId: number): Observable<BaseResponseDto<void>> {
    return this._httpClient.post<BaseResponseDto<void>>(
      `${ this._baseUrl }/deviceProfile/${ deviceProfileId }/alarmRule/${ alarmRuleId }`,
      {},
    );
  }

  public removeAlarmRule(deviceProfileId: number, alarmRuleId: number): Observable<BaseResponseDto<void>> {
    return this._httpClient.delete<BaseResponseDto<void>>(
      `${ this._baseUrl }/deviceProfile/${ deviceProfileId }/alarmRule/${ alarmRuleId }`,
      {},
    );
  }

  private listenToDeviceProfileUpdates(): void {
    this._websocketService.subscribe(DomainEnum.DEVICEPROFILE, 'update', (payload: WebsocketResponseDto) => {
      if (payload.params && payload.params['id']) {
        this.fetchDeviceProfile(payload.params['id']);
      } else {
        this.fetchDeviceProfiles();
      }
    });
  }
}


